import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTheme } from '@/theme';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/common/DataTable';
import * as liteLogApi from '@/api/device/logs/liteLog';
import * as miniLogApi from '@/api/device/logs/miniLog';
import { SummaryCard } from '@/components/ui/common/SummaryCard';
import { formatDateTime } from '@/utils/date';
import {
  TabLayout,
  TabHeader,
  ErrorMessage,
  EmptyState,
  LoadingSkeleton,
  DownloadButtons,
} from '@/components/layout/TabLayout';
import { useLogTabState, useLogFetch } from './hooks/useLogTabState';
import { useTranslation } from 'react-i18next';
import { TimelineChart } from './timeline/TimelineChart';
import {
  buildFullTimeline,
  CATEGORY_I18N_KEY,
  formatDuration,
  calcTotalByCategory,
  type TimelineCategory,
} from './timeline/timelineUtils';

interface Device {
  serial_number: string;
  device_type?: string;
}

interface EventLogTabProps {
  device: Device;
  onExportData?: (data: {
    chartImageUrl?: string;
    tableData: any[];
    tableColumns: Array<{ header: string; accessorKey: string }>;
  }) => void;
  onDownloadPdf?: () => Promise<void>;
}

interface EventLog {
  id: bigint | number;
  log_id: string;
  serial_number: string;
  record_index: number;
  timestamp: string;
  event_type: number;
  event_type_text: string;
  description: string | null;
  is_error: boolean;
  is_critical: boolean;
  // Lite specific
  event_data1?: bigint | number | null;
  event_data1_text?: string | null;
  event_data2?: number | null;
  event_data2_text?: string | null;
  event_data3?: number | null;
  event_data3_text?: string | null;
  // Mini specific
  log_type?: number;
  event_data?: number | null;
  event_data_label?: string | null;
}

export const EventLogTab: React.FC<EventLogTabProps> = ({
  device,
  onExportData,
  onDownloadPdf,
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation();

  const {
    loading, setLoading,
    error, setError, handleError,
    dateRange, setDateRange,
    isLite, isMini,
    shouldFetch,
  } = useLogTabState({ device });

  const [logs, setLogs] = useState<EventLog[]>([]);
  const excelExportRef = React.useRef<(() => void) | null>(null);
  const chartRef = useRef<any>(null);

  const fetchEventLogs = async () => {
    if (!device.serial_number || !dateRange.from || !dateRange.to) return;

    setLoading(true);
    setError(null);

    try {
      let data: EventLog[] = [];
      if (isLite) {
        data = await liteLogApi.getEventLogsByDevice(
          device.serial_number,
          dateRange.from,
          dateRange.to,
          10000
        );
      } else if (isMini) {
        data = await miniLogApi.getEventLogsByDevice(
          device.serial_number,
          dateRange.from,
          dateRange.to,
          10000
        );
      }

      setLogs(data);
    } catch (err: unknown) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  useLogFetch({
    shouldFetch,
    deps: [device.serial_number, device.device_type, dateRange.from, dateRange.to],
    fetchFn: fetchEventLogs,
  });

  const getEventTypeColor = (log: EventLog): string => {
    if (log.is_critical) {
      return theme.colors.status.danger.fill.surface as string;
    } else if (log.is_error) {
      return theme.colors.status.caution.fill.surface as string;
    } else {
      return theme.colors.status.stable.fill.surface as string;
    }
  };

  const formatDateTimeStr = (dateString: string): string => {
    return formatDateTime(dateString);
  };

  // 엑셀 다운로드를 위한 플랫 데이터 구조
  interface FlatEventLog {
    timestamp: string;
    timestamp_formatted: string;
    event_type: number;
    event_type_text: string;
    event_data_text: string;
    description: string;
    severity: string;
  }

  const flattenedLogs = useMemo<FlatEventLog[]>(() => {
    return logs.map(log => {
      let eventDataText = '';
      if (isLite) {
        if (log.event_data1_text) eventDataText += log.event_data1_text;
        if (log.event_data2_text) {
          if (eventDataText) eventDataText += ', ';
          eventDataText += log.event_data2_text;
        }
        if (log.event_data3_text) {
          if (eventDataText) eventDataText += ', ';
          eventDataText += log.event_data3_text;
        }
      } else if (isMini) {
        if (log.event_data_label) {
          eventDataText = log.event_data_label;
        }
      }

      let severity = 'Normal';
      if (log.is_critical) severity = 'Critical';
      else if (log.is_error) severity = 'Error';

      return {
        timestamp: log.timestamp,
        timestamp_formatted: formatDateTimeStr(log.timestamp),
        event_type: log.event_type,
        event_type_text: log.event_type_text,
        event_data_text: eventDataText || '-',
        description: log.description || '-',
        severity,
      };
    });
  }, [logs, isLite, isMini]);

  // 테이블 컬럼 정의
  const columns = useMemo<ColumnDef<FlatEventLog>[]>(
    () => [
      {
        accessorKey: 'timestamp_formatted',
        header: () => 'Time',
        size: 180,
      },
      {
        accessorKey: 'event_type_text',
        header: () => 'Event Type',
        cell: ({ row }) => {
          const log = logs.find(
            l => formatDateTimeStr(l.timestamp) === row.original.timestamp_formatted
          );
          if (!log) return row.original.event_type_text;

          const color = getEventTypeColor(log);
          const label = row.original.event_type_text;
          return (
            <span
              style={{
                padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
                borderRadius: theme.borderRadius.sm,
                backgroundColor: color + '20',
                color: color,
                fontSize: theme.fontSize.xs,
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </span>
          );
        },
        size: 180,
      },
      {
        accessorKey: 'event_data_text',
        header: () => 'Event Data',
        size: 200,
      },
      {
        accessorKey: 'severity',
        header: () => 'Severity',
        cell: ({ row }) => {
          const severity = row.original.severity;
          let color = theme.colors.text.secondary;
          if (severity === 'Critical') color = theme.colors.status.danger.stroke.text as string;
          else if (severity === 'Error') color = theme.colors.status.caution.stroke.text as string;
          else color = theme.colors.status.stable.stroke.text as string;

          return (
            <span
              style={{
                padding: `${theme.spacing[2]} ${theme.spacing[3]}`,
                borderRadius: theme.borderRadius.sm,
                backgroundColor: color + '20',
                color: color,
                fontSize: theme.fontSize.xs,
                fontWeight: 600,
              }}
            >
              {severity}
            </span>
          );
        },
        size: 100,
      },
      {
        accessorKey: 'description',
        header: () => 'Description',
        size: 300,
      },
    ],
    [logs, theme]
  );

  // Timeline data
  const timeline = useMemo(() => {
    if (!dateRange.from || !dateRange.to || logs.length === 0) return null;
    return buildFullTimeline(logs, dateRange.from, dateRange.to);
  }, [logs, dateRange.from, dateRange.to]);

  const totals = useMemo(() => {
    if (!timeline) return null;
    return calcTotalByCategory(timeline.intervals);
  }, [timeline]);

  const hasChargingData = useMemo(() => {
    if (!timeline) return false;
    return timeline.intervals.some(i => i.category === 'charging');
  }, [timeline]);

  const categories: TimelineCategory[] = hasChargingData
    ? ['operating', 'ble', 'charging']
    : ['operating', 'ble'];

  // PDF 생성을 위한 데이터 export
  useEffect(() => {
    if (!onExportData) return;
    if (loading) return;
    if (logs.length === 0) {
      onExportData({ tableData: [], tableColumns: [] });
      return;
    }

    const prepareExportData = async () => {
      let chartImageUrl: string | undefined;

      if (chartRef.current) {
        try {
          await new Promise(resolve => setTimeout(resolve, 1000));
          const echartsInstance = chartRef.current.getEchartsInstance();
          if (echartsInstance) {
            const isReady = echartsInstance.getWidth() > 0 && echartsInstance.getHeight() > 0;
            if (isReady) {
              chartImageUrl = echartsInstance.getDataURL({ type: 'png', pixelRatio: 2 });
            }
          }
        } catch (error) {
          console.error('[EventLogTab] Failed to get chart image:', error);
        }
      }

      const tableData = flattenedLogs.map(row => ({
        Time: row.timestamp_formatted,
        'Event Type': row.event_type_text,
        'Event Data': row.event_data_text,
        Severity: row.severity,
        Description: row.description,
      }));

      const tableColumns = [
        { header: 'Time', accessorKey: 'Time' },
        { header: 'Event Type', accessorKey: 'Event Type' },
        { header: 'Event Data', accessorKey: 'Event Data' },
        { header: 'Severity', accessorKey: 'Severity' },
        { header: 'Description', accessorKey: 'Description' },
      ];

      onExportData({ chartImageUrl, tableData, tableColumns });
    };

    const timer = setTimeout(prepareExportData, 1000);
    return () => clearTimeout(timer);
  }, [onExportData, logs, flattenedLogs, loading]);

  // Excel 다운로드 핸들러
  const handleExcelDownload = () => {
    if (excelExportRef.current) {
      excelExportRef.current();
    }
  };

  return (
    <TabLayout>
      {/* 날짜 범위 선택 */}
      <TabHeader dateRange={dateRange} onDateRangeChange={setDateRange} />

      {/* 에러 상태 */}
      {error && !loading && <ErrorMessage message={error} />}

      {loading ? (
        <LoadingSkeleton count={10} showChart={true} />
      ) : logs.length === 0 ? (
        <EmptyState message="No event logs in selected period" />
      ) : (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing[4],
            width: '100%',
          }}
        >
          {/* Timeline chart + summary */}
          {timeline && timeline.intervals.length > 0 && (
            <>
              {/* Summary cards */}
              {totals && (
                <SummaryCard
                  size="compact"
                  items={categories.map(cat => ({
                    label: t(CATEGORY_I18N_KEY[cat]),
                    value: formatDuration(totals[cat]),
                  }))}
                />
              )}

              {/* Timeline chart */}
              <div
                style={{
                  background: theme.colors.surface.layout.background,
                  borderRadius: theme.borderRadius.md,
                  border: `1px solid ${theme.colors.border.container}`,
                  padding: theme.spacing[4],
                  flexShrink: 0,
                }}
              >
                <TimelineChart
                  timeline={timeline}
                  showCharging={hasChargingData}
                  chartRef={chartRef}
                />
              </div>
            </>
          )}

          {/* Event log table */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              width: '100%',
              borderRadius: theme.borderRadius.md,
              position: 'relative',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                overflow: 'auto',
              }}
            >
              <div
                style={{
                  width: '100%',
                  borderRadius: theme.borderRadius.md,
                }}
              >
                <DataTable
                  data={flattenedLogs}
                  columns={columns}
                  enablePagination={true}
                  pageSize={20}
                  pageSizeOptions={[10, 20, 50, 100]}
                  enableSorting={true}
                  emptyMessage="No event log data"
                  enableExcelExport={false}
                  excelFileName={`event_log_${device.serial_number}_${dateRange.from}_${dateRange.to}`}
                  onExcelExportReady={fn => {
                    excelExportRef.current = fn;
                  }}
                />
              </div>
            </div>
          </div>
          <DownloadButtons
            onDownloadExcel={handleExcelDownload}
            onDownloadPdf={onDownloadPdf}
            disabled={loading || logs.length === 0}
          />
        </div>
      )}
    </TabLayout>
  );
};
